import _ from 'lodash';
/**
 * 递归累加计算
 *
 * @param {*} item
 * @param {*} childrenKeyString 子节点key
 * @param {*} countTotalKeyString 计算值key，为空则计算节点累加
 */
function countTotal(item, childrenKeyString = 'children', key, countTotalKeyString) {
    return (item[childrenKeyString] || []).reduce((sum, child) => {
        child[key] = countTotal(child, childrenKeyString, key, countTotalKeyString);
        return (sum += child[key]);
    }, countTotalKeyString ? item[countTotalKeyString] || 0 : 1);
}
/**
 * list 转成 tree
 *
 * @param {*} list 传入数组
 * @param {*} rootValue 根值，一般为 -root
 * @param {*} idKey 节点ID
 * @param {*} parentIdKey 节点的PID
 * @param {*} childrenKeyString 子节点的名称，undefined默认是 children
 * @param {*} counts 用于计算包含自己在内的节点数量，如果传入一个key，那么会对节点上的key的值进行累加
 * * { _total: '', _count: 'countMe' }
 * * _total将会对应包含自己在内的节点数量
 * * _count将会对节点上的countMe的值进行累加
 */
export function listToTree(list, rootValue, idKey, parentIdKey, childrenKeyString = 'children', counts = {}) {
    const _list = _.cloneDeep(list);
    const tree = _list.filter(father => {
        const branchList = _list.filter(child => father[idKey] === child[parentIdKey]);
        if (branchList.length > 0) {
            father[childrenKeyString] = branchList;
        }
        return father[parentIdKey] === rootValue;
    });
    // 附加额外计算属性
    return tree.map(item => {
        for (const key in counts) {
            item[key] = countTotal(item, childrenKeyString, key, counts[key]);
        }
        return item;
    });
}
/**
 * 通过对应的key和value找出tree的节点数据
 *
 * @param {*} tree 传入tree
 * @param {*} key 目标的key
 * @param {*} value 目标key对应的value
 * @param {*} childrenKeyString tree的子名称
 */
export function findTreeNode(tree = [], key, value, childrenKeyString = 'children') {
    let stack = null;
    if (_.isPlainObject(tree)) {
        stack = [{ ...tree }];
    }
    else if (_.isArray(tree)) {
        stack = [...tree];
    }
    else {
        return null;
    }
    while (stack.length) {
        const curr = stack.pop();
        if (curr[key] === value) {
            return curr;
        }
        if (curr[childrenKeyString]) {
            stack.push(...curr[childrenKeyString]);
        }
    }
    return null;
}
/**
 * tree 转成 list
 * 注意！ 此方法转换后节点的顺序为反向,又非深度优先！非常奇怪！
 *
 * @param {*} tree 传入tree
 * @param {*} childrenKeyString tree的子名称
 * @param {*} selfKeyString 节点的ID名称
 * @param {*} parentKeyString 节点的PID名称
 * @param {*} rootValueString 根节点值，一般为 -root
 */
export function treeToList(tree = [], childrenKeyString = 'children', selfKeyString = 'id', parentKeyString = 'parent', rootValueString = '-root') {
    let stack = null;
    if (_.isPlainObject(tree)) {
        stack = [{ ...tree }];
    }
    else if (_.isArray(tree)) {
        stack = [...tree];
    }
    else {
        return null;
    }
    const list = [];
    const parent = parentKeyString;
    while (stack.length) {
        const curr = stack.pop();
        const _curr = {
            ...curr,
            [parent]: curr[parentKeyString] || rootValueString,
        };
        delete _curr[childrenKeyString];
        list.unshift(_curr);
        if (curr[childrenKeyString]) {
            const pid = _curr[selfKeyString];
            const children = _.reduce(curr[childrenKeyString], (result, item) => {
                item[parent] = pid;
                return [...result, item];
            }, []);
            stack.push(...children);
        }
    }
    return list;
}
/**
 * tree 转成 list
 *
 * 转换后节点顺序为正向遍历深度优先
 *
 * @param {*} tree 传入tree
 * @param {*} childrenKeyString tree的子名称
 * @param {*} selfKeyString 节点的ID名称
 * @param {*} parentKeyString 节点的PID名称
 * @param {*} rootValueString 根节点值，一般为 -root
 */
export function treeToListByDFS(tree = [], childrenKeyString = 'children', selfKeyString = 'id', parentKeyString = 'parent', rootValueString = '-root') {
    const list = [];
    function deepUnfold(nodes, parentKey) {
        _.forEach(nodes, (item) => {
            list.push(item);
            item[parentKeyString] = parentKey;
            if (item[childrenKeyString] && item[childrenKeyString].length) {
                deepUnfold(item[childrenKeyString], item[selfKeyString]);
            }
        });
        return list;
    }
    return deepUnfold(tree, rootValueString);
}
/**
 * 把list 转成 map，以某个list的key作为map的key
 *
 * @param {*} list 传入list
 * @param {*} keyString list的key作为map的key
 */
export function listToMap(list = [], keyString = 'id') {
    return _.reduce(list, (ret, item) => {
        return {
            ...ret,
            [item[keyString]]: item,
        };
    }, {});
}
/**
 * 以某个key筛选出一个只包含对应的多个值的树
 *
 * @param {*} tree 传入tree
 * @param {*} filterKey 需要筛选的key
 * @param {*} filterValues 需要筛选的值(数组)
 * @param {*} childrenKeyString 子节点名称
 * @param {*} selfKeyString 节点ID名称
 * @param {*} parentKeyString 节点PID名称
 * @param {*} rootValueString 根值
 */
export function filterTree(tree = [], filterKey = 'id', filterValues = [], childrenKeyString = 'children', selfKeyString = 'id', parentKeyString = 'parent', rootValueString = '-root') {
    if (!filterValues)
        return [];
    const cloneFilterValues = [...filterValues];
    const list = treeToList(tree, childrenKeyString, selfKeyString, parentKeyString, rootValueString);
    const cloneList = [...list];
    const retList = [];
    while (cloneFilterValues.length) {
        const curr = cloneFilterValues.pop();
        const item = _.find(cloneList, { [filterKey]: curr });
        if (item) {
            _.pull(cloneList, item);
            retList.unshift(item);
            cloneFilterValues.push(item[parentKeyString]);
        }
    }
    const intersectionList = _.intersection(list, retList);
    const retTree = listToTree(intersectionList, rootValueString, selfKeyString, parentKeyString, childrenKeyString);
    return retTree;
}
